import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { useBranding } from '../context/BrandingContext';
import type { ColorMode } from '../branding/types';
import {
  DEFAULT_LOGO_DARK,
  DEFAULT_LOGO_LIGHT,
  LOGO_BRAND_ACCENT_DARK,
  LOGO_BRAND_ACCENT_LIGHT,
  resolveMediaUrl,
} from '../branding/applyBrandingCss';
import LogoCropModal from './LogoCropModal';
import { NAV_LOGO_ASPECT } from '../branding/navLogoAspect';

const BrandingSettingsPanel: React.FC = () => {
  const { branding, refresh } = useBranding();
  const [colorMode, setColorMode] = useState<ColorMode>('brand');
  const [customLight, setCustomLight] = useState('#6366f1');
  const [customDark, setCustomDark] = useState('#818cf8');
  const [logoLight, setLogoLight] = useState('');
  const [logoDark, setLogoDark] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<'light' | 'dark' | null>(null);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [cropSession, setCropSession] = useState<{
    which: 'light' | 'dark';
    objectUrl: string;
    file: File;
  } | null>(null);

  const endCropSession = useCallback(() => {
    setCropSession((prev) => {
      if (prev?.objectUrl) URL.revokeObjectURL(prev.objectUrl);
      return null;
    });
  }, []);

  useEffect(() => {
    setColorMode(branding.color_mode);
    setCustomLight(branding.custom_accent_light);
    setCustomDark(branding.custom_accent_dark);
    setLogoLight(branding.logo_light_url || '');
    setLogoDark(branding.logo_dark_url || '');
  }, [branding]);

  const previewLight = resolveMediaUrl(logoLight || null, DEFAULT_LOGO_LIGHT);
  const previewDark = resolveMediaUrl(logoDark || null, DEFAULT_LOGO_DARK);

  const uploadLogo = async (which: 'light' | 'dark', file: File) => {
    setUploading(which);
    setMessage(null);
    try {
      const body = new FormData();
      body.append('image', file);
      const res = await api.post<{ url: string }>('/uploads', body);
      const url = res.data.url;
      if (which === 'light') setLogoLight(url);
      else setLogoDark(url);
      setMessage({ type: 'ok', text: 'Image uploaded. Save branding to apply everywhere.' });
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setMessage({ type: 'err', text: err.response?.data?.error || 'Upload failed' });
    } finally {
      setUploading(null);
    }
  };

  const onFileChosen = (which: 'light' | 'dark', file: File | null) => {
    if (!file) return;
    const objectUrl = URL.createObjectURL(file);
    setCropSession({ which, objectUrl, file });
  };

  const handleCroppedOrOriginal = async (which: 'light' | 'dark', file: File) => {
    endCropSession();
    await uploadLogo(which, file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      await api.put('/branding', {
        logo_light_url: logoLight.trim() || null,
        logo_dark_url: logoDark.trim() || null,
        color_mode: colorMode,
        custom_accent_light: customLight,
        custom_accent_dark: customDark,
      });
      await refresh();
      setMessage({ type: 'ok', text: 'Branding saved.' });
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } };
      setMessage({ type: 'err', text: e.response?.data?.error || 'Failed to save branding' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
      <div className="px-4 py-5 sm:p-6 space-y-6">
        <div>
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Portal branding</h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Logos and accent colors apply across the portal, including the sign-in page. Use a version on a{' '}
            <strong>light</strong> background for light mode and one on a <strong>dark</strong> background for dark mode.
          </p>
        </div>

        <div className="rounded-lg border border-brand-border bg-brand-muted dark:bg-brand-muted/30 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 space-y-2">
          <p className="font-medium text-gray-900 dark:text-gray-100">Top navigation logo shape</p>
          <p>
            The header reserves a wide horizontal strip for your logo—about <strong>{NAV_LOGO_ASPECT}:1</strong> (width to
            height), in the same range as roughly <strong>4:1</strong> to <strong>5:1</strong>. Supplying or cropping to
            that ratio makes the wordmark <strong>fill the bar</strong> instead of shrinking inside a large empty area.
          </p>
          <p className="text-gray-600 dark:text-gray-400">
            When you upload a file, the crop tool is locked to <strong>{NAV_LOGO_ASPECT}:1</strong> so the export matches
            that slot. Use zoom so the logo reaches the left and right edges of the frame, then save branding.
          </p>
        </div>

        {cropSession && (
          <LogoCropModal
            imageSrc={cropSession.objectUrl}
            originalFile={cropSession.file}
            title={cropSession.which === 'light' ? 'Crop light mode logo' : 'Crop dark mode logo'}
            onCancel={endCropSession}
            onConfirmCropped={(file) => handleCroppedOrOriginal(cropSession.which, file)}
            onUploadOriginal={(file) => handleCroppedOrOriginal(cropSession.which, file)}
          />
        )}

        {message && (
          <div
            className={
              message.type === 'ok'
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200 px-4 py-3 rounded text-sm'
                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded text-sm'
            }
          >
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid gap-8 sm:grid-cols-2">
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Light mode logo</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Preview uses the same proportions as the top bar ({NAV_LOGO_ASPECT}:1).
              </p>
              <div
                className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50 overflow-hidden flex items-center justify-start px-2"
                style={{
                  height: '3rem',
                  width: `min(100%, calc(3rem * ${NAV_LOGO_ASPECT}))`,
                }}
              >
                <img src={previewLight} alt="Light logo preview" className="h-full w-full object-contain object-left" />
              </div>
              <input
                type="file"
                accept="image/*"
                className="mt-2 block w-full text-sm text-gray-600 dark:text-gray-400"
                disabled={!!uploading || !!cropSession}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  onFileChosen('light', f || null);
                }}
              />
              <label className="mt-2 block text-xs font-medium text-gray-500 dark:text-gray-400">Image URL (optional)</label>
              <input
                type="text"
                value={logoLight}
                onChange={(e) => setLogoLight(e.target.value)}
                placeholder="Leave empty for bundled default"
                className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-brand focus:border-brand"
              />
              <button
                type="button"
                className="mt-2 text-sm text-brand hover:underline"
                onClick={() => setLogoLight('')}
              >
                Use default logo file
              </button>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Dark mode logo</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Preview uses the same proportions as the top bar ({NAV_LOGO_ASPECT}:1).
              </p>
              <div
                className="rounded-lg border border-gray-600 bg-gray-900 overflow-hidden flex items-center justify-start px-2"
                style={{
                  height: '3rem',
                  width: `min(100%, calc(3rem * ${NAV_LOGO_ASPECT}))`,
                }}
              >
                <img src={previewDark} alt="Dark logo preview" className="h-full w-full object-contain object-left" />
              </div>
              <input
                type="file"
                accept="image/*"
                className="mt-2 block w-full text-sm text-gray-600 dark:text-gray-400"
                disabled={!!uploading || !!cropSession}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  onFileChosen('dark', f || null);
                }}
              />
              <label className="mt-2 block text-xs font-medium text-gray-500 dark:text-gray-400">Image URL (optional)</label>
              <input
                type="text"
                value={logoDark}
                onChange={(e) => setLogoDark(e.target.value)}
                placeholder="Leave empty for bundled default"
                className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-brand focus:border-brand"
              />
              <button
                type="button"
                className="mt-2 text-sm text-brand hover:underline"
                onClick={() => setLogoDark('')}
              >
                Use default logo file
              </button>
            </div>
          </div>

          <fieldset>
            <legend className="text-sm font-medium text-gray-900 dark:text-gray-100">Accent colors</legend>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 mb-4">
              Buttons, links, and focus rings use the accent. Choose presets that match the official logos, or pick your own
              for light and dark mode.
            </p>
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="colorMode"
                  checked={colorMode === 'brand'}
                  onChange={() => setColorMode('brand')}
                  className="mt-1 text-brand focus:ring-brand"
                />
                <span>
                  <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">Match logo colors</span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Light mode: {LOGO_BRAND_ACCENT_LIGHT} · Dark mode: {LOGO_BRAND_ACCENT_DARK}
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="colorMode"
                  checked={colorMode === 'custom'}
                  onChange={() => setColorMode('custom')}
                  className="mt-1 text-brand focus:ring-brand"
                />
                <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">Custom colors</span>
              </label>
            </div>

            {colorMode === 'custom' && (
              <div className="mt-4 grid gap-4 sm:grid-cols-2 pl-7">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Light mode accent</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={customLight.length === 7 ? customLight : '#6366f1'}
                      onChange={(e) => setCustomLight(e.target.value)}
                      className="h-9 w-14 rounded border border-gray-300 dark:border-gray-600 cursor-pointer bg-white dark:bg-gray-700"
                    />
                    <input
                      type="text"
                      value={customLight}
                      onChange={(e) => setCustomLight(e.target.value)}
                      pattern="^#[0-9A-Fa-f]{6}$"
                      className="flex-1 border border-gray-300 dark:border-gray-600 rounded-md py-2 px-3 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono focus:ring-brand focus:border-brand"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Dark mode accent</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={customDark.length === 7 ? customDark : '#818cf8'}
                      onChange={(e) => setCustomDark(e.target.value)}
                      className="h-9 w-14 rounded border border-gray-300 dark:border-gray-600 cursor-pointer bg-white dark:bg-gray-700"
                    />
                    <input
                      type="text"
                      value={customDark}
                      onChange={(e) => setCustomDark(e.target.value)}
                      pattern="^#[0-9A-Fa-f]{6}$"
                      className="flex-1 border border-gray-300 dark:border-gray-600 rounded-md py-2 px-3 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono focus:ring-brand focus:border-brand"
                    />
                  </div>
                </div>
              </div>
            )}
          </fieldset>

          <div>
            <button
              type="submit"
              disabled={saving || !!uploading || !!cropSession}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save branding'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BrandingSettingsPanel;
